/**
 * The report catalogue — names, formats, routes and the prompt that builds
 * each of the six documents. One table, read by the reports index, the report
 * scenes' actions, and the assistant's `generate_report`, so the three never
 * disagree about what a report is called or where it opens.
 *
 * Port of the demo's `RLABEL` / `RFMT` (demo 2556–2557), the six options of
 * the empty report pane (demo 2654) and the prompt strings its Regenerate and
 * "Same report for" buttons sent (demo 2657–2658).
 *
 * Routes live here rather than in `oem-routes.ts` because a report address
 * carries two parts (kind and mine) that `routeForView(view, sub)` has no
 * slot for; `routeForView('reports')` still lands on the index.
 *
 * Engine layer (layer 2): pure data and string builders. No React.
 *
 * @see src/versions/v1/schema/report.ts
 * @see docs/logic/reports/overview.md
 */

import { MINES, MINE_ORDER } from '../data/core.ts';
import type { MineCode } from '../schema/machine.ts';
import type {
  ReportDownload,
  ReportFormat,
  ReportKind,
  ReportRef,
  WeeklyReportKind,
} from '../schema/report.ts';

/** Demo 2556. `RLABEL` — the long label, used on the generation steps. */
export const REPORT_LABEL: Readonly<Record<ReportKind, string>> = {
  sos: 'S.O.S daily report · Service Operations Summary',
  email: 'Machine risk summary · email',
  drills: 'Weekly status · Underground Drilling',
  loadhaul: 'Weekly status · Load & Haul',
  surface: 'Weekly status · Surface Drilling',
  deck: 'Equipment summary · presentation',
};

/** Short title per kind — the chat card and the "which report?" answer. */
export const REPORT_TITLE: Readonly<Record<ReportKind, string>> = {
  email: 'Machine risk summary email',
  drills: 'Weekly status — Underground Drilling',
  loadhaul: 'Weekly status — Load & Haul',
  surface: 'Weekly status — Surface Drilling',
  deck: 'Equipment summary presentation',
  sos: 'S.O.S daily report',
};

/** Demo 2557. `RFMT` — the format tag each document carried. */
export const REPORT_FORMAT: Readonly<Record<ReportKind, ReportFormat>> = {
  sos: 'docx',
  email: 'eml',
  drills: 'docx',
  loadhaul: 'docx',
  surface: 'docx',
  deck: 'pptx',
};

/**
 * What Download produces in this port. The demo shipped the exported DOCX and
 * PPTX as base64 blobs; those were dropped at extract time, so the weekly
 * report and the deck download as a self-contained HTML rendering of the same
 * document, and the email as the `.eml` the demo built. The S.O.S report was
 * in-app only in the demo too.
 */
export const REPORT_DOWNLOAD: Readonly<Record<ReportKind, ReportDownload>> = {
  sos: null,
  email: 'eml',
  drills: 'html',
  loadhaul: 'html',
  surface: 'html',
  deck: 'html',
};

export const WEEKLY_KINDS = ['drills', 'loadhaul', 'surface'] as const satisfies readonly WeeklyReportKind[];

/** The division each weekly report covers — `B.reports[kind].division`. */
export const WEEKLY_DIVISION: Readonly<Record<WeeklyReportKind, string>> = {
  drills: 'Underground Drilling',
  loadhaul: 'Load & Haul',
  surface: 'Surface Drilling',
};

/** Demo 2602. The weekly report's document-id stem. */
export const WEEKLY_DOC_PREFIX: Readonly<Record<WeeklyReportKind, string>> = {
  drills: 'WS-UGD',
  loadhaul: 'WS-LH',
  surface: 'WS-SD',
};

/** Demo 2677. The mine the email and the deck fall back to. */
export const DEFAULT_REPORT_MINE = { email: 'MI', deck: 'BR' } as const satisfies Record<
  'email' | 'deck',
  MineCode
>;

export const isReportKind = (x: unknown): x is ReportKind =>
  x === 'email' || x === 'drills' || x === 'loadhaul' || x === 'surface' || x === 'deck' || x === 'sos';

export const isWeeklyKind = (x: unknown): x is WeeklyReportKind =>
  x === 'drills' || x === 'loadhaul' || x === 'surface';

export const isMineCode = (x: unknown): x is MineCode =>
  typeof x === 'string' && (MINE_ORDER as readonly string[]).includes(x);

/**
 * Demo 2677 — normalise a kind and an optional mine into an address: an
 * unknown kind reads as Load & Haul, and the email and deck fall back to
 * their default mines, exactly as `generateReport` did.
 */
export function reportRef(kind: string, mine?: string | null): ReportRef {
  const code = mine ? mine.toUpperCase() : null;
  const m = isMineCode(code) ? code : null;
  if (kind === 'sos') return { kind: 'sos', mine: m };
  if (kind === 'email') return { kind: 'email', mine: m ?? DEFAULT_REPORT_MINE.email };
  if (kind === 'deck') return { kind: 'deck', mine: m ?? DEFAULT_REPORT_MINE.deck };
  return { kind: isWeeklyKind(kind) ? kind : 'loadhaul', mine: null };
}

/** Where a report opens. */
export function reportPath(ref: ReportRef): string {
  switch (ref.kind) {
    case 'sos':
      return ref.mine ? `/reports/sos?site=${ref.mine}` : '/reports/sos';
    case 'email':
      return `/reports/email/${ref.mine}`;
    case 'deck':
      return `/reports/deck/${ref.mine}`;
    default:
      return `/reports/weekly/${ref.kind}`;
  }
}

/** The inverse of {@link reportPath}, for a pathname (query ignored). */
export function reportRefFromPath(pathname: string): ReportRef | null {
  const path = pathname.split('?')[0].replace(/\/+$/, '');
  const m = /^\/reports\/(email|deck|weekly)\/([^/]+)$/.exec(path);
  if (!m) return path === '/reports/sos' ? { kind: 'sos', mine: null } : null;
  if (m[1] === 'weekly') return isWeeklyKind(m[2]) ? { kind: m[2], mine: null } : null;
  const mine = m[2].toUpperCase();
  if (!isMineCode(mine)) return null;
  return m[1] === 'email' ? { kind: 'email', mine } : { kind: 'deck', mine };
}

/**
 * Demo 2654 and 2657 — the question that builds a report. Sent by the index
 * cards, Regenerate and "Same report for"; `local-answer.ts`'s
 * `reportIntent` parses every one of them back to the same address.
 */
export function reportPrompt(ref: ReportRef): string {
  switch (ref.kind) {
    case 'sos':
      return `Generate today's S.O.S report${ref.mine ? ` for ${MINES[ref.mine].name}` : ''}`;
    case 'email':
      return `Write the machine risk email for ${MINES[ref.mine].name}`;
    case 'deck':
      return `Create the ${MINES[ref.mine].name} equipment summary presentation`;
    default:
      return `Generate the weekly status report for ${WEEKLY_DIVISION[ref.kind]}`;
  }
}

/** One card on the reports index — demo 2654. */
export interface ReportCard {
  readonly ref: ReportRef;
  readonly title: string;
  readonly desc: string;
}

/** Demo 2654. The six cards, in the demo's order and words. */
export const REPORT_CARDS: readonly ReportCard[] = [
  {
    ref: { kind: 'email', mine: 'MI' },
    title: 'Risk summary email',
    desc: "for a mine's technical team — flagged machines, actions, parts below minimum",
  },
  {
    ref: { kind: 'drills', mine: null },
    title: 'Weekly status · Underground Drilling',
    desc: 'availability, breakdowns, attention list, PM due, energy',
  },
  {
    ref: { kind: 'loadhaul', mine: null },
    title: 'Weekly status · Load & Haul',
    desc: 'same sections for loaders and trucks',
  },
  {
    ref: { kind: 'surface', mine: null },
    title: 'Weekly status · Surface Drilling',
    desc: 'top-hammer and blasthole drills at Ochre Hill and Gawler Deep',
  },
  {
    ref: { kind: 'sos', mine: null },
    title: 'S.O.S daily report',
    desc: 'hotspots, activity, work in progress, evaluation and prompt — all sites, this morning',
  },
  {
    ref: { kind: 'deck', mine: 'BR' },
    title: 'Equipment summary deck',
    desc: '8-slide review of one mine — cost per hour, condition, energy, recommendations',
  },
];

/**
 * Demo 2658 — "Same report for": the other addresses of the same kind. Every
 * other mine for the email and the deck; the other two divisions for a
 * weekly report (the demo offered none there, which left the reader typing).
 */
export function siblingRefs(ref: ReportRef): ReportRef[] {
  if (ref.kind === 'email' || ref.kind === 'deck') {
    const kind = ref.kind;
    return MINE_ORDER.filter((m) => m !== ref.mine).map((mine) => ({ kind, mine }));
  }
  if (isWeeklyKind(ref.kind)) {
    return WEEKLY_KINDS.filter((k) => k !== ref.kind).map((kind) => ({ kind, mine: null }));
  }
  return [];
}

/** The label a sibling button reads. */
export function siblingLabel(ref: ReportRef): string {
  if (ref.kind === 'email' || ref.kind === 'deck') return MINES[ref.mine].name;
  if (isWeeklyKind(ref.kind)) return WEEKLY_DIVISION[ref.kind];
  return ref.mine ? MINES[ref.mine].name : 'All sites';
}

/** Demo 2560. `slug` — a name as a file-name segment. */
export const reportSlug = (s: string): string =>
  s.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** The date every generated file name carries — the snapshot's morning. */
export const REPORT_FILE_DATE = '2026-09-09';

/** Demo `now()` at generation — the pack's `week.generated` clock time. */
export const REPORT_GENERATED_AT = '06:05';
